import { useMemo } from "react";
import { scopedProjectKey, scopeProjectRef } from "@cmmd-forge/client-runtime";
import type { SidebarThreadSummary } from "../../types";
import { isCmmdEmbedMode } from "../../embed/cmmdEmbedMode";
import {
  filterThreadsToEmbedWorkspace,
  projectMatchesEmbedWorkspace,
} from "../../embed/cmmdEmbedWorkspaceScoping";
import { useCurrentForgeWorkspaceState } from "../../workspace/currentForgeWorkspaceStore";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";
import type { SidebarProjectWorkspaceIdentity } from "./SidebarProjectItem";
import { resolveThreadWorkspaceKey, type WorkspaceRef } from "./workspaceScoping";

export function useEmbedScopedSidebarThreads(input: {
  readonly sidebarThreads: readonly SidebarThreadSummary[];
  readonly taskWorkspaceIndex: ReadonlyMap<string, WorkspaceRef>;
  readonly projectWorkspaceIndex: ReadonlyMap<string, WorkspaceRef>;
  readonly campaignWorkspaceIndex: ReadonlyMap<string, WorkspaceRef>;
  readonly workspaceKeyByTaskTitle: ReadonlyMap<string, string>;
  readonly workspaceIdentityByKey: ReadonlyMap<string, SidebarProjectWorkspaceIdentity>;
}): {
  readonly embedMode: boolean;
  readonly embedWorkspaceId: number | null;
  readonly scopedSidebarThreads: readonly SidebarThreadSummary[];
  readonly workspaceIdentityByPhysicalProjectKey: ReadonlyMap<
    string,
    SidebarProjectWorkspaceIdentity
  >;
} {
  const embedMode = isCmmdEmbedMode();
  const { workspaceId: embedWorkspaceId } = useCurrentForgeWorkspaceState();
  const scopedSidebarThreads = useMemo(() => {
    if (!embedMode || embedWorkspaceId === null) return input.sidebarThreads;
    return filterThreadsToEmbedWorkspace({
      threads: input.sidebarThreads,
      workspaceId: embedWorkspaceId,
      taskWorkspaceIndex: input.taskWorkspaceIndex,
      projectWorkspaceIndex: input.projectWorkspaceIndex,
      campaignWorkspaceIndex: input.campaignWorkspaceIndex,
      workspaceKeyByTaskTitle: input.workspaceKeyByTaskTitle,
    });
  }, [
    embedMode,
    embedWorkspaceId,
    input.campaignWorkspaceIndex,
    input.projectWorkspaceIndex,
    input.sidebarThreads,
    input.taskWorkspaceIndex,
    input.workspaceKeyByTaskTitle,
  ]);
  const workspaceIdentityByPhysicalProjectKey = useMemo(() => {
    const map = new Map<string, SidebarProjectWorkspaceIdentity>();
    for (const thread of scopedSidebarThreads) {
      const workspaceKey = resolveThreadWorkspaceKey({
        thread,
        taskWorkspaceIndex: input.taskWorkspaceIndex,
        projectWorkspaceIndex: input.projectWorkspaceIndex,
        campaignWorkspaceIndex: input.campaignWorkspaceIndex,
        workspaceKeyByTaskTitle: input.workspaceKeyByTaskTitle,
      });
      if (!workspaceKey) continue;
      const identity = input.workspaceIdentityByKey.get(workspaceKey);
      if (!identity) continue;
      const projectKey = scopedProjectKey(scopeProjectRef(thread.environmentId, thread.projectId));
      if (!map.has(projectKey)) map.set(projectKey, identity);
    }
    return map;
  }, [
    input.campaignWorkspaceIndex,
    input.projectWorkspaceIndex,
    input.taskWorkspaceIndex,
    input.workspaceIdentityByKey,
    input.workspaceKeyByTaskTitle,
    scopedSidebarThreads,
  ]);
  return {
    embedMode,
    embedWorkspaceId,
    scopedSidebarThreads,
    workspaceIdentityByPhysicalProjectKey,
  };
}

export function useEmbedScopedSidebarProjects(input: {
  readonly embedMode: boolean;
  readonly embedWorkspaceId: number | null;
  readonly sortedProjects: readonly SidebarProjectSnapshot[];
  readonly sidebarProjects: readonly SidebarProjectSnapshot[];
}): {
  readonly embedScopedSortedProjects: readonly SidebarProjectSnapshot[];
  readonly embedScopedSidebarProjects: readonly SidebarProjectSnapshot[];
} {
  const embedScopedSortedProjects = useMemo(() => {
    const workspaceId = input.embedWorkspaceId;
    if (!input.embedMode || workspaceId === null) return input.sortedProjects;
    return input.sortedProjects.filter((project) =>
      projectMatchesEmbedWorkspace({
        workspaceIdentityId: project.source.workspaceIdentity?.workspaceId ?? null,
        workspaceId,
      }),
    );
  }, [input.embedMode, input.embedWorkspaceId, input.sortedProjects]);

  const embedScopedSidebarProjects = useMemo(() => {
    const workspaceId = input.embedWorkspaceId;
    if (!input.embedMode || workspaceId === null) return input.sidebarProjects;
    return input.sidebarProjects.filter((project) =>
      projectMatchesEmbedWorkspace({
        workspaceIdentityId: project.source.workspaceIdentity?.workspaceId ?? null,
        workspaceId,
      }),
    );
  }, [input.embedMode, input.embedWorkspaceId, input.sidebarProjects]);

  return { embedScopedSortedProjects, embedScopedSidebarProjects };
}
